/**
 * @file target-completion.tsx
 * @module app/modules/sales-management/sales-representative/dashboard/chart/target-completion
 * @description Implements the target completion interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Col, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { useAuth } from "../../../../auth";
import APICallService from "../../../../../../api/apiCallService";
import { assistAdmin } from "../../../../../../api/apiEndPoints";
import { AllCustomers } from "../../../../../../utils/constants";

const getBgColor = (percentage: number) => {
  if (percentage === 100) return "#15803d"; // green
  if (percentage > 50) return "#6366F1"; // blue
  return "#FF9800"; // orange
};

const renderTooltip = (props: any, item: any, remaining: number) => (
  <Popover
    {...props}
    id="popover-basic"
    className="border-0 shadow"
    style={{ ...props.style, minWidth: 200 }}
  >
    <Popover.Body className="p-0">
      {/* Name and Target */}
      <div className="mb-2 px-4 py-2">
        <span className="d-block text-black fs-16 fw-semibold mb-1">
          {item.name}
        </span>
        <span className="d-block fw-medium fs-16 text-dark">
          Tsh {item.target.toLocaleString()}
        </span>
      </div>

      <div className="separator border-gray-300"></div>

      {/* Achieved */}
      <div className="mb-2 px-4 py-2">
        <span className="d-block text-black fs-16 fw-semibold mb-1">
          Achieved
        </span>
        <span className="d-block fw-medium fs-16 text-dark">
          Tsh {item.achieved.toLocaleString()}
        </span>
      </div>

      <div className="separator border-gray-300"></div>

      {/* Remaining */}
      <div className="mb-2 px-4 py-2">
        <span className="d-block text-danger fs-16 fw-semibold mb-1">
          Remaining
        </span>
        <span className="d-block fw-medium fs-16 text-dark">
          Tsh {remaining.toLocaleString()}
        </span>
      </div>
    </Popover.Body>
  </Popover>
);

const TargetCompletion = () => {
  const [series, setSeries] = useState([0]);
  const { currentUser } = useAuth();

  useEffect(() => {
    (async () => {
      await fetchProfileData();
    })();
  }, []);
  const fetchProfileData = async () => {
    const repId = currentUser?._id ? currentUser?._id : "";
    let apiService = new APICallService(
      assistAdmin.info,
      "",
      { id: repId },
      "",
      "",
      "",
      "",
      AllCustomers,
    );
    let response = await apiService.callAPI();
    if (response) {
      const percentage = Math.min(100, Number(((response.currentSalesTarget?.achieved / response.currentSalesTarget?.target) * 100).toFixed(2)));
      setSeries(percentage ? [percentage] : [0]);
    }
  };
  return (
    <Row className="align-items-center my-7 gy-6">
      {/* <Col sm={12} md={6} lg={6}>
        <div
          className="px-5 py-5"
          style={{
            border: "0.5px solid #E0E0DF",
            borderRadius: "10px",
            background: "#F9F9F9",
          }}
        >
          <h3 className="fw-bolder fs-18 mb-4">
            Target completion by sales reps
          </h3>

          <div
            style={{
              maxHeight: data.length > 3 ? 180 : "auto",
              overflowY: data.length > 3 ? "auto" : "visible",
              paddingRight: 6,
            }}
          >
            {data.map((item, index) => {
              const remaining = Method.getGeneralizedAmount(Math.max(
                  0,
                  (item?.currentSalesTarget?.target ?? 0) -
                  (item?.currentSalesTarget?.achieved ?? 0)
                ))

              return (
                <div key={index} className="d-flex align-items-center mb-3">
                  <div
                    className="flex-grow-1 me-3"
                    style={{
                      height: 44,
                      background: "#fff",
                      borderRadius: 10,
                    }}
                  >
                    <OverlayTrigger
                      placement="bottom"
                      overlay={(props) => renderTooltip(props, item, remaining)}
                      trigger={["hover", "focus"]}
                    >
                      <div
                        className="h-100 d-flex align-items-center fw-semibold fs-16 text-white px-3"
                        style={{
                          width: `${item.percentage}%`,
                          background: getBgColor(item.percentage),
                          borderRadius: 10,
                          cursor: "pointer",
                        }}
                      >
                        {item.name}
                      </div>
                    </OverlayTrigger>
                  </div>

                  <div className="fw-semibold">{item.percentage}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </Col> */}

      <Col sm={12} md={6} lg={6}>
        <div
          className="px-5 py-5"
          style={{
            border: "0.5px solid #E0E0DF",
            borderRadius: "10px",
            background: "#F9F9F9",
            maxHeight: '270px'
          }}
        >
          <h3 className="fw-bolder fs-18 mb-4 px-2 text-dark">
            Target completion rate
          </h3>
          <div
            id="chart"
            className="d-flex justify-content-center position-relative"
          >
            <Chart
              options={{
                chart: {
                  type: "radialBar",
                  offsetY: -20,
                  sparkline: {
                    enabled: true,
                  },
                },
                plotOptions: {
                  radialBar: {
                    startAngle: -90,
                    endAngle: 90,
                    hollow: {
                      margin: 0,
                      size: "50%",
                      background: "transparent",
                      image: undefined,
                    },
                    track: {
                      background: "#111827", // Dark almost black
                      strokeWidth: "100%",
                      margin: 0,
                      opacity: 1,
                    },
                    dataLabels: {
                      show: true,
                      name: {
                        show: false,
                      },
                      value: {
                        offsetY: -5,
                        fontSize: "32px",
                        fontWeight: 700,
                        color: "#000000",
                        formatter: function (val) {
                          return val + "%";
                        },
                      },
                    },
                  },
                },
                fill: {
                  type: "gradient",
                  gradient: {
                    shade: "dark",
                    type: "horizontal",
                    shadeIntensity: 0.5,
                    gradientToColors: ["#2B43FF"],
                    inverseColors: true,
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 100],
                  },
                },
                colors: ["#8492FF"],
                stroke: {
                  lineCap: "butt",
                },
                labels: ["Target completion"],
              }}
              series={series}
              type="radialBar"
              height={350}
            />
            {/* small tick needle at arc edge */}
            {(() => {
              const value = Array.isArray(series) ? series[0] : 0;
              const angle = -90 + (value / 100) * 180; // -90..90
              const chartHeight = 350;
              const outerRadius = chartHeight / 2; // px
              const tickHeight = 80; // short tick length
              const marginTop = -(outerRadius - 8); // move tick to arc edge (tweakable)

              return (
                <div style={{ position: "absolute", top: "110%", left: "58%", transform: "translate(-50%, -50%)", pointerEvents: "none", zIndex: 3 }}>
                  {/* <div
                    style={{
                      width: 6,
                      height: tickHeight,
                      transform: `rotate(${angle}deg)`,
                      transformOrigin: "50% 100%",
                      background: "#2B43FF",
                      borderRight: "2px solid #071227",
                      borderRadius: 3,
                      marginTop: marginTop,
                      boxShadow: "0 2px 6px rgba(43,67,255,0.5)",
                    }}
                  /> */}
                </div>
              );
            })()}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default TargetCompletion;
